import React, { useEffect, useState, useRef } from 'react';
import Chart from 'react-apexcharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useSelector } from 'react-redux';

const GenderPieChart = () => {
  const genderStatusFilter = useSelector(state => state.dashboard?.genderStatusCount);
  const [genderSeries, setGenderSeries] = useState([]);
  const [genderLabels, setGenderLabels] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const chartContainerRef = useRef(null);
  const modalChartContainerRef = useRef(null);

  useEffect(() => {
    if (genderStatusFilter && Array.isArray(genderStatusFilter.series) && Array.isArray(genderStatusFilter.labels)) {
      const defaultLabels = ['male', 'female', 'other'];

      const fullSeries = defaultLabels.map(label => {
        const index = genderStatusFilter.labels.indexOf(label);
        return index !== -1 ? genderStatusFilter.series[index] : 0;
      });

      setGenderLabels(defaultLabels);
      setGenderSeries(fullSeries);
    } else {
      setGenderLabels(['male', 'female', 'other']);
      setGenderSeries([0, 0, 0]);
    }
  }, [genderStatusFilter]);

  const options = {
    chart: {
      type: 'pie',
    },
    labels: genderLabels,
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'dark',
        type: "vertical",
        shadeIntensity: 0.5,
        inverseColors: false,
        opacityFrom: 0.9,
        opacityTo: 1,
        stops: [0, 100],
      },
    },
    colors: ["#780206", "#061161", "#3A6073"],
    legend: {
      position: 'top',
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            width: '100%',
          },
          legend: {
            position: 'bottom',
          },
        },
      },
    ],
  };

  const exportPDF = async (isModal = false) => {
    const targetRef = isModal ? modalChartContainerRef : chartContainerRef;

    if (!targetRef.current) return;

    const canvas = await html2canvas(targetRef.current, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF('landscape', 'pt', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 40, pdfWidth, pdfHeight);
    pdf.save('gender-distribution.pdf');
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-md flex flex-col h-full w-full">
        <div className="border-b px-4 py-3 rounded-t-lg gradiant2 flex justify-between items-center">
          <h3 className="text-[0.875rem] tracking-[0.0625rem] text-[#856800] font-bold uppercase">
            Gender Distribution
          </h3>
        </div>

        <div className="p-4 w-full flex justify-center items-center">
          <div
            ref={chartContainerRef}
            className="cursor-pointer"
            onClick={() => setShowModal(true)}
            title="Click to view fullscreen"
          >
            <Chart
              options={options}
              series={genderSeries}
              type="pie"
              width="400"
            />
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[9999] bg-white flex flex-col items-center justify-center">
          <button
            className="absolute top-4 left-6 text-3xl font-bold text-gray-600 hover:text-black"
            onClick={() => setShowModal(false)}
          >
            ×
          </button>

          <div className="absolute top-4 right-6">
            <div className="relative inline-block text-left">
              <div className="origin-top-right absolute right-0 mt-2 w-40 rounded-md gradiant2 shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                <div className="py-1">
                  <button
                    onClick={() => exportPDF(true)}
                    className="w-full px-4 py-2 text-sm text-[#856800] font-bold"
                  >
                    Export PDF
                  </button>
                </div>
              </div>
            </div>
          </div>

          <h2 className="text-xl font-semibold mb-4 text-[#856800] mt-8">
            Gender Distribution
          </h2>

          <div
            ref={modalChartContainerRef}
            className="w-[90vw] h-[80vh] flex items-center justify-center"
          >
            <Chart
              options={options}
              series={genderSeries}
              type="pie"
              width={1000}
              height={500}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default GenderPieChart;
